import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgentAvatar } from './AgentAvatars';
import AgentWorkplaceAnimation from './AgentWorkplaceAnimation';
import SignupModal from './SignupModal';
import { Zap, Shield, Clock, Users, BarChart3, ArrowRight, CheckCircle, Star } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';

const AI_EMPLOYEES = [
  { id: 'kathy', name: 'Kathy', title: 'AI HR Generalist', desc: 'Onboarding, recordkeeping, HR inquiries & policy admin' },
  { id: 'sophie', name: 'Sophie', title: 'AI Talent Sourcer', desc: 'Candidate sourcing, job postings & resume screening' },
  { id: 'john', name: 'John', title: 'AI Compliance Analyst', desc: 'Regulatory monitoring, compliance checks & audits' },
  { id: 'omar', name: 'Omar', title: 'AI Payroll Coordinator', desc: 'Payroll processing, deductions & payment summaries' },
  { id: 'priya', name: 'Priya', title: 'AI Benefits Administrator', desc: 'Benefits enrollment, eligibility & open enrollment' },
  { id: 'mario', name: 'Mario', title: 'AI Employee Relations', desc: 'Employee concerns, warning letters & exit interviews' },
  { id: 'linda', name: 'Linda', title: 'AI L&D Lead', desc: 'Training design, learning paths & progress tracking' },
  { id: 'alex', name: 'Alex', title: 'AI Performance Manager', desc: 'Reviews, self-assessments & performance reports' },
  { id: 'felix', name: 'Felix', title: 'AI Scheduling Assistant', desc: 'Interview scheduling & calendar management' },
  { id: 'jasmine', name: 'Jasmine', title: 'AI D&I Specialist', desc: 'Diversity metrics, D&I communications & initiatives' },
  { id: 'megan', name: 'Megan', title: 'AI Content Creator', desc: 'Training manuals, presentations & learning materials' },
];

const PLAN_FEATURES = {
  starter: { messages: '100/mo', features: ['2 AI Employees', '100 messages/month', 'Document upload', 'Basic reports', 'Email support'], color: '#2E7D32', popular: false },
  professional: { messages: '1,000/mo', features: ['6 AI Employees', '1,000 messages/month', 'RAG document context', 'Advanced reports & export', 'HRIS integrations', 'Priority support'], color: '#7B1FA2', popular: true },
  enterprise: { messages: 'Unlimited', features: ['All 11 AI Employees', 'Unlimited messages', 'SSO/OIDC integration', 'Custom prompt templates', 'Dedicated support', 'API access'], color: '#F9A825', popular: false },
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([
    { id: 'starter', name: 'Starter', price: 49, agents: 2, ...PLAN_FEATURES.starter },
    { id: 'professional', name: 'Professional', price: 149, agents: 6, ...PLAN_FEATURES.professional },
    { id: 'enterprise', name: 'Enterprise', price: 399, agents: 11, ...PLAN_FEATURES.enterprise },
  ]);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${backendUrl}/api/pricing`).then(r => {
      if (r.data) {
        const updated = Object.entries(r.data).map(([id, p]) => ({
          id, name: p.name, price: p.price, agents: p.agents,
          ...(PLAN_FEATURES[id] || PLAN_FEATURES.starter)
        }));
        if (updated.length > 0) setPlans(updated);
      }
    }).catch(e => console.error('Pricing load error:', e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const [showSignup, setShowSignup] = useState(false);
  const [signupPlan, setSignupPlan] = useState('professional');

  return (
    <div className="landing-page">
      {showSignup && <SignupModal onClose={() => setShowSignup(false)} initialPlan={signupPlan} />}      {/* Navbar */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={LOGO_URL} alt="Panzix" style={{ width: 40, height: 40, borderRadius: 10 }} />
            <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Panzix HR AI Workforce</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#team">AI Team</a>
            <a href="#pricing">Pricing</a>
            <a href="/help" style={{ color: '#475569', textDecoration: 'none', fontWeight: 500 }}>Help Center</a>
            <button className="btn-action" onClick={() => setShowSignup(true)} data-testid="landing-signin" style={{ padding: '10px 24px', fontSize: 14 }}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-badge">Powered by Panzix AI</div>
          <h1>Your AI-Powered<br />HR Department</h1>
          <p>Deploy autonomous AI employees that handle onboarding, compliance, payroll, talent sourcing, and more. Panzix HR AI Workforce transforms your HR operations with intelligent agents that work 24/7.</p>
          <div style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
            <button className="btn-action" onClick={() => setShowSignup(true)} style={{ padding: '16px 32px', fontSize: 16, borderRadius: 14 }} data-testid="landing-get-started">
              Get Started Free <ArrowRight size={18} style={{ marginLeft: 8 }} />
            </button>
            <a href="#team" className="btn-secondary" style={{ padding: '16px 32px', fontSize: 16, borderRadius: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              Meet the AI Team
            </a>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 32, flexWrap: 'wrap' }}>
            {[{ icon: Users, text: '11 AI Employees' }, { icon: Clock, text: 'Available 24/7' }, { icon: Shield, text: 'SOC2 Ready' }].map(f => (
              <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 14 }}>
                <f.icon size={18} color="var(--panzix-green)" />{f.text}
              </div>
            ))}
          </div>
        </div>
        <div className="landing-hero-visual">
          <AgentWorkplaceAnimation />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="landing-section" style={{ background: '#f8fafc' }}>
        <div className="landing-container">
          <div className="section-center-header">
            <h2>Everything You Need to Automate HR</h2>
            <p>From hiring to compliance, our AI employees handle the heavy lifting so your team can focus on strategy.</p>
          </div>
          <div className="features-grid">
            {[
              { icon: Zap, title: 'Instant Deployment', desc: 'Deploy AI employees in seconds with our 3-step wizard. No training needed.', color: '#2E7D32' },
              { icon: Users, title: 'Inter-Agent Collaboration', desc: 'AI employees consult each other for cross-functional insights and better results.', color: '#7B1FA2' },
              { icon: Shield, title: 'Enterprise Security', desc: 'PII masking, audit logs, RBAC, and SOC2-ready compliance built in.', color: '#1565C0' },
              { icon: BarChart3, title: 'Real-time Analytics', desc: 'Track tasks completed, hours saved, and performance metrics in real-time.', color: '#F9A825' },
              { icon: Clock, title: 'Document Generation', desc: 'AI creates professional documents (Word & PDF) on demand with your branding.', color: '#00897B' },
              { icon: Star, title: 'RAG-Enhanced Context', desc: 'Upload company policies and handbooks. AI grounds decisions in your rules.', color: '#D32F2F' },
            ].map(f => (
              <div key={f.title} className="feature-card">
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${f.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <f.icon size={24} color={f.color} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the AI Team */}
      <section id="team" className="landing-section">
        <div className="landing-container">
          <div className="section-center-header">
            <h2>Meet Your AI Team</h2>
            <p>11 specialized AI employees, each an expert in their domain. Deploy the ones you need.</p>
          </div>
          <div className="team-grid">
            {AI_EMPLOYEES.map(a => (
              <div key={a.id} className="team-card" onClick={() => navigate(`/agent/${a.id}`)} style={{ cursor: 'pointer' }} data-testid={`team-${a.id}`}>
                <AgentAvatar agentType={a.id} size={72} />
                <h4>{a.name}</h4>
                <p className="team-title">{a.title}</p>
                <p className="team-desc">{a.desc}</p>
                <div style={{ marginTop: 8, fontSize: 12, color: '#2E7D32', fontWeight: 600 }}>Learn more &rarr;</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="landing-section" style={{ background: '#f8fafc' }}>
        <div className="landing-container">
          <div className="section-center-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Start free. Scale as your team grows. No hidden fees.</p>
          </div>
          <div className="pricing-grid">
            {plans.map(plan => (
              <div key={plan.name} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <h3 style={{ color: plan.color }}>{plan.name}</h3>
                <div className="pricing-amount">${plan.price}<span>/mo</span></div>
                <p className="pricing-agents">{plan.agents} AI Employees &middot; {plan.messages} messages</p>
                <ul className="pricing-features">
                  {plan.features.map(f => (
                    <li key={f}><CheckCircle size={16} color="var(--panzix-green)" />{f}</li>
                  ))}
                </ul>
                <button className={`btn-action ${plan.popular ? '' : 'outline'}`} onClick={() => { setSignupPlan(plan.id || 'professional'); setShowSignup(true); }}
                  style={{ width: '100%', padding: '14px', fontSize: 15, borderRadius: 12, ...(plan.popular ? {} : { background: 'white', color: '#374151', border: '2px solid #e2e8f0' }) }}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <div className="landing-container" style={{ textAlign: 'center' }}>
          <h2>Ready to Transform Your HR?</h2>
          <p>Join innovative companies using AI to revolutionize human resources.</p>
          <button className="btn-action" onClick={() => setShowSignup(true)} style={{ padding: '16px 40px', fontSize: 16, borderRadius: 14, marginTop: 24 }}>
            Start Your Free Trial <ArrowRight size={18} style={{ marginLeft: 8 }} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={LOGO_URL} alt="Panzix" style={{ width: 32, height: 32, borderRadius: 8 }} />
              <span style={{ fontWeight: 700, color: 'white' }}>Panzix Global HR Solutions, LLC</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/hz8a6ej1_privacy-policy-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Privacy Policy</a>
              <a href="https://customer-assets.emergentagent.com/job_ai-employees-6/artifacts/c94tjqmn_terms-of-service-panzix-hr-ai-workforce.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Terms of Service</a>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>&copy; {new Date().getFullYear()} Panzix HR AI Workforce</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
